import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Table, Dropdown, Spinner } from "react-bootstrap";
import { FiChevronLeft, FiMapPin, FiMail, FiPhone, FiUser, FiDownload, FiFileText } from "react-icons/fi";
import { getCustomerEditApi, updateCustomerStatusApi, deleteCustomerApi } from "./customer-api";
import DeleteModal from "../../common/deleteModel";
import moreIcon from "../../../assets/svg/more_ic.svg";
import { useGlobal } from "../../../GlobalContext";
import CommonSnackbar from "../../common/reusable-commponent";
import { sanitizeInput, sanitizeNumericInput, validateUrl } from "../../common/security-utils";
import "../../common/skeleton-effect.css";
const CustomerView = () => {
  const { rolePermissionList, userType } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state?.id;
  const [activeTab, setActiveTab] = useState("gate");
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });
  const triggerSnackbar = (message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  };
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  };

  const handleDownload = async (url, fileName) => {
    if (!validateUrl(url)) {
      triggerSnackbar("Invalid file URL", false);
      return;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback for CORS or other issues
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    }
  };

  function checkPermission(permission) {
    return rolePermissionList.findIndex((element) => element["name"] === "Customer" && element["menu_permission"] === true && element[permission] === true) > -1 ? true : false;
  }

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const response = await getCustomerEditApi(id);
          if (response.data && response.data.success) {
            setCustomer(response.data.customers);
          } else {
            triggerSnackbar(response.data?.message || "Failed to fetch customer data", false);
          }
        } catch {
          triggerSnackbar("An error occurred while fetching customer data", false);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [id]);

  const handleStatusUpdate = async (newStatus) => {
    if (!customer) return;
    setIsStatusUpdating(true);
    try {
      const response = await updateCustomerStatusApi(customer.id, {
        status: newStatus,
      });
      if (response.data && response.data.success) {
        setCustomer((prev) => ({ ...prev, status: newStatus }));
        triggerSnackbar(response.data.message || "Status updated successfully", true);
      } else {
        triggerSnackbar(response.data?.message || "Failed to update status", false);
      }
    } catch {
      triggerSnackbar("An error occurred while updating status", false);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!customer) return;
    try {
      const response = await deleteCustomerApi(customer.id);
      if (response.data && response.data.success) {
        setIsOpenDelete(false);
        triggerSnackbar(response.data.message || "Customer deleted successfully", true);
        setTimeout(() => navigate(-1), 500);
      } else {
        triggerSnackbar(response.data?.message || "Failed to delete customer", false);
      }
    } catch {
      triggerSnackbar("An error occurred while deleting customer", false);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex"
        style={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div
          style={{
            width: "calc(100vw - 80px)",
            overflowY: "auto",
            height: "100vh",
          }}
        >
          <div className="p-3">
            {/* Header Skeleton */}
            <div className="bg-white rounded shadow-sm p-3 mb-3">
              <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex gap-3">
                  <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }} />
                  <div className="d-flex gap-3">
                    <div className="skeleton" style={{ width: "64px", height: "64px", borderRadius: "50%" }} />
                    <div className="d-flex flex-column gap-2">
                      <div className="skeleton skeleton-text" style={{ width: "180px", height: "18px" }} />
                      <div className="skeleton skeleton-text-sm" style={{ width: "120px", height: "12px" }} />
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <div className="skeleton skeleton-button" style={{ width: "100px", height: "32px" }} />
                  <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 mt-3">
                <div className="skeleton skeleton-text" style={{ width: "150px", height: "16px" }} />
                <div className="skeleton skeleton-text" style={{ width: "200px", height: "16px" }} />
              </div>
            </div>

            {/* Cards Skeleton */}
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="skeleton skeleton-text-lg mb-4" style={{ width: "140px" }} />
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <div className="skeleton skeleton-text-sm" style={{ width: "90px" }} />
                        <div className="skeleton skeleton-text" style={{ width: "100px" }} />
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <div className="skeleton skeleton-text-sm" style={{ width: "140px" }} />
                        <div className="skeleton skeleton-text" style={{ width: "80px" }} />
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-2">
                        <div className="skeleton skeleton-text-sm" style={{ width: "70px" }} />
                        <div className="skeleton skeleton-text" style={{ width: "120px" }} />
                      </div>
                    </div>
                    <div className="border rounded-4 p-3" style={{ borderStyle: "dashed" }}>
                      <div className="skeleton skeleton-text-sm mb-3" style={{ width: "180px" }} />
                      <div className="d-flex gap-3">
                        <div className="skeleton" style={{ width: "48px", height: "48px", borderRadius: "50%" }} />
                        <div className="flex-grow-1">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "140px" }} />
                          <div className="skeleton skeleton-text-sm" style={{ width: "200px" }} />
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <div className="d-flex flex-column gap-3 h-100">
                  <Card className="border-0 shadow-sm flex-fill">
                    <Card.Body className="p-4">
                      <div className="skeleton skeleton-text-lg mb-3" style={{ width: "120px" }} />
                      <div className="d-flex gap-3">
                        <div className="skeleton" style={{ width: "32px", height: "32px", borderRadius: "50%" }} />
                        <div className="flex-grow-1">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "100%" }} />
                          <div className="skeleton skeleton-text-sm" style={{ width: "80%" }} />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>

                  <Card className="border-0 shadow-sm flex-fill">
                    <Card.Body className="p-4">
                      <div className="skeleton skeleton-text-lg mb-3" style={{ width: "130px" }} />
                      <div className="d-flex gap-3">
                        <div className="skeleton" style={{ width: "32px", height: "32px", borderRadius: "50%" }} />
                        <div className="flex-grow-1">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "100%" }} />
                          <div className="skeleton skeleton-text-sm" style={{ width: "80%" }} />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>

            {/* Bottom Section Skeleton */}
            <div className="bg-white rounded shadow-sm p-3">
              <div className="d-flex gap-2 mb-3">
                <div className="skeleton skeleton-button" style={{ width: "100px", height: "36px", borderRadius: "20px" }} />
                <div className="skeleton skeleton-button" style={{ width: "120px", height: "36px", borderRadius: "20px" }} />
                <div className="skeleton skeleton-button" style={{ width: "140px", height: "36px", borderRadius: "20px" }} />
              </div>
              <div className="border rounded p-3">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className="d-flex gap-3 mb-3">
                    <div className="skeleton skeleton-text" style={{ width: "25%", height: "16px" }} />
                    <div className="skeleton skeleton-text" style={{ width: "25%", height: "16px" }} />
                    <div className="skeleton skeleton-text" style={{ width: "25%", height: "16px" }} />
                    <div className="skeleton skeleton-text" style={{ width: "25%", height: "16px" }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <p>Customer not found.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div className="mt-2">
        <div className="bg-white rounded shadow-sm p-3 mb-3">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex gap-3">
              <Button variant="light" className="rounded-circle d-flex align-items-center justify-content-center border" style={{ width: "40px", height: "40px" }} onClick={() => navigate(-1)}>
                <FiChevronLeft size={20} />
              </Button>

              <div className="d-flex gap-3">
                {/* Profile Photo */}
                <div
                  className="rounded-circle overflow-hidden d-flex align-items-center justify-content-center"
                  style={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: customer.profile || customer.profilephoto ? "transparent" : "#E8F0FE",
                    border: "2px solid #E8F0FE",
                  }}
                >
                  {customer.profile || customer.profilephoto ? (
                    <img
                      src={validateUrl(customer.profile || customer.profilephoto) ? (customer.profile || customer.profilephoto) : ""}
                      alt={sanitizeInput(customer.first_name)}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <FiUser size={32} color="#3469E7" />
                  )}
                </div>

                <div className="text-start">
                  <h4 className="m-0 fw-bold fs-18 mb-2">{sanitizeInput(customer.first_name)}</h4>
                  <div className="fs-12 text-muted">{sanitizeInput(customer.type)}</div>
                  {/* Second Row - Mobile Number and Email */}
                  {/* <div className="d-flex align-items-center gap-3 mb-1">
                      <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                        <FiPhone className="text-primary" />
                        {customer.phonenumber}
                      </div>
                      <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                        <FiMail className="text-primary" />
                        {customer.email}
                      </div>
                    </div> */}

                  {/* Third Row - Type */}
                </div>
              </div>
            </div>

            <div className="d-flex flex-column align-items-end gap-2">
              <div className="d-flex align-items-center gap-2">
                {checkPermission("edit_permission") && (
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="white"
                      className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2 fs-13"
                      style={{
                        color: customer.status ? "#198754" : "#dc3545",
                      }}
                    >
                      {isStatusUpdating ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <span className={`rounded-circle ${customer.status ? "bg-success" : "bg-danger"}`} style={{ width: "8px", height: "8px" }}></span>
                          {customer.status ? "Active" : "Inactive"}
                        </>
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleStatusUpdate(true)}>Active</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleStatusUpdate(false)}>Inactive</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                {/* <Button
                    variant="primary"
                    className="px-3 py-1 fs-13 rounded"
                    style={{
                      backgroundColor: "#3469E7",
                      borderColor: "#3469E7",
                    }}
                    onClick={() =>
                      navigate("/customer/create", {
                        state: { id: customer.id },
                      })
                    }
                  >
                    Edit
                  </Button> */}

                {/* <Dropdown>
                    <Dropdown.Toggle
                      variant="white"
                      className="border rounded-circle d-flex align-items-center justify-content-center p-0"
                      style={{ width: "32px", height: "32px" }}
                    >
                      <FiMoreVertical />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item
                        onClick={() =>
                          navigate("/customer/create", {
                            state: { id: customer.id },
                          })
                        }
                        className="fs-13 d-flex align-items-center gap-2"
                      >
                        <FiEdit size={16} />
                        Edit
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={() => setIsOpenDelete(true)}
                        className="fs-13 text-danger d-flex align-items-center gap-2"
                      >
                        <FiTrash2 size={16} />
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown> */}
                <div className="d-flex flex-row flex-grow-1 align-items-end justify-content-end gap-2 ps-3">
                  <div onClick={(e) => e.stopPropagation()}>
                    {(checkPermission("edit_permission") || checkPermission("delete_permission")) && (
                      <Dropdown>
                        <Dropdown.Toggle
                          bsPrefix="custom-toggle"
                          variant="light"
                          id="dropdown-sort"
                          className="d-flex align-items-center justify-content-between rounded-5 bg-white"
                          style={{
                            width: "36px",
                            height: "36px",
                            border: "1px solid #EBEBEB",
                          }}
                        >
                          <span className="d-flex align-items-center px-1 gap-2">
                            <img src={moreIcon} alt="more" />
                          </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            border: "1px solid #EBEBEB",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {checkPermission("edit_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={() => {
                                navigate("/customer/create", {
                                  state: { id: customer.id },
                                });
                              }}
                            >
                              Edit
                            </Dropdown.Item>
                          )}
                          {checkPermission("delete_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={() => {
                                setIsOpenDelete(true);
                              }}
                            >
                              Delete
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between w-100 mt-2">
            {/* Left Section (Phone + Email) */}
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                <FiPhone className="text-primary" />
                {customer.phonenumber?.trim() ? sanitizeInput(customer.phonenumber) : "-"}
              </div>

              <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                <FiMail className="text-primary" />
                {customer.email?.trim() ? sanitizeInput(customer.email) : "-"}
              </div>
            </div>
          </div>
        </div>

        <Row className="g-3 mb-3">
          {/* Customer Details Card */}
          <Col md={6}>
            <Card className="border-0 shadow-sm h-100 text-start">
              <Card.Body className="p-4">
                <h6 className="fw-bold mb-4">Customer details</h6>

                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted fs-14 d-flex align-items-center gap-2">
                    Customer ID
                  </span>
                  <span className="fw-bold fs-14">{sanitizeInput(customer.code)}</span>
                </div>

                <div className="d-flex justify-content-between mb-4">
                  <span className="text-muted fs-14 d-flex align-items-center gap-2">
                    Total Purchase Amount
                  </span>
                  <span className="fw-bold fs-14">₹{sanitizeInput(String(customer.total_trip_amount || 0))}</span>
                </div>
                <div className="d-flex justify-content-between mb-4">
                  <span className="text-muted fs-14 d-flex align-items-center gap-2">GST NO</span>
                  <span className="fw-bold fs-14">{customer.gst_number ? sanitizeInput(customer.gst_number) : "-"}</span>
                </div>
                <div className="border rounded-4 p-3 bg-white" style={{ borderStyle: "dashed" }}>
                  <p className="text-muted fs-12 mb-3">Bank Account Information</p>
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-18"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#E8F0FE",
                        color: "#3469E7",
                      }}
                    >
                      {customer.bank_account_name ? sanitizeInput(customer.bank_account_name.charAt(0).toUpperCase()) : "N"}
                    </div>
                    <div>
                      <div className="fw-bold fs-14">{customer.bank_account_name ? sanitizeInput(customer.bank_account_name) : "-"}</div>
                      <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                        {customer.bank_account_number ? sanitizeInput(customer.bank_account_number) : "-"}
                        <span className="text-muted">•</span>
                        <span className="badge bg-white text-primary border px-2 py-1 fw-normal">IFSC : {customer.ifsc_code ? sanitizeInput(customer.ifsc_code) : "-"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Address Cards */}
          <Col md={6}>
            <div className="d-flex flex-column gap-3 h-100 text-start">
              <Card className="border-0 shadow-sm flex-fill">
                <Card.Body className="p-4">
                  <h6 className="fw-bold mb-3">Billing address</h6>
                  <div className="d-flex gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#FFE5E5",
                      }}
                    >
                      <FiMapPin color="#FF3B30" size={16} />
                    </div>
                    <div>
                      <p className="fs-14 text-muted m-0 lh-base">{sanitizeInput(customer.billing_address)}</p>
                      <p className="fs-13 text-muted m-0">
                        {sanitizeInput(customer.billing_city)}, {sanitizeInput(customer.billing_state)}, {sanitizeInput(customer.billing_country)} - {sanitizeInput(customer.billing_zipcode)}
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm flex-fill">
                <Card.Body className="p-4">
                  <h6 className="fw-bold mb-3">Shipping address</h6>
                  <div className="d-flex gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#FFE5E5",
                      }}
                    >
                      <FiMapPin color="#FF3B30" size={16} />
                    </div>
                    <div>
                      <p className="fs-14 text-muted m-0 lh-base">{sanitizeInput(customer.shipping_address)}</p>
                      <p className="fs-13 text-muted m-0">
                        {sanitizeInput(customer.shipping_city)}, {sanitizeInput(customer.shipping_state)}, {sanitizeInput(customer.shipping_country)} - {sanitizeInput(customer.shipping_zipcode)}
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>

        {/* Bottom Section */}
        <div className="bg-white rounded shadow-sm p-3">
          <div className="d-flex gap-2 mb-3">
            <Button
              variant={activeTab === "gate" ? "primary" : "light"}
              className={`rounded-pill px-4 fs-14 fw-500 ${activeTab === "gate" ? "" : "text-muted bg-light border-0"}`}
              onClick={() => setActiveTab("gate")}
              style={
                activeTab === "gate"
                  ? {
                    backgroundColor: "#E8F0FE",
                    color: "#3469E7",
                    borderColor: "#E8F0FE",
                  }
                  : {}
              }
            >
              Gate ({customer.gate ? customer.gate.length : 0})
            </Button>
            <Button
              variant={activeTab === "contacts" ? "primary" : "light"}
              className={`rounded-pill px-4 fs-14 fw-500 ${activeTab === "contacts" ? "" : "text-muted bg-light border-0"}`}
              onClick={() => setActiveTab("contacts")}
              style={
                activeTab === "contacts"
                  ? {
                    backgroundColor: "#E8F0FE",
                    color: "#3469E7",
                    borderColor: "#E8F0FE",
                  }
                  : {}
              }
            >
              Contacts ({customer.contact ? customer.contact.length : 0})
            </Button>
            <Button
              variant={activeTab === "attachments" ? "primary" : "light"}
              className={`rounded-pill px-4 fs-14 fw-500 ${activeTab === "attachments" ? "" : "text-muted bg-light border-0"}`}
              onClick={() => setActiveTab("attachments")}
              style={
                activeTab === "attachments"
                  ? {
                    backgroundColor: "#E8F0FE",
                    color: "#3469E7",
                    borderColor: "#E8F0FE",
                  }
                  : {}
              }
            >
              Attachments ({customer.Attachments ? customer.Attachments.length : 0})
            </Button>
          </div>

          <div className="table-responsive bg-light rounded border-0">
            {activeTab === "gate" && (
              <Table hover className="m-0">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Gate Name</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0 text-center" style={{ width: "50px" }}>
                    </th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Gate In</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0 text-center" style={{ width: "50px" }}>
                    </th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Gate Out</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0 text-center" style={{ width: "50px" }}>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {customer.gate &&
                    customer.gate.map((row, index) => (
                      <tr key={index} className="bg-white border-bottom">
                        <td className="py-3 px-4 fs-13 fw-500">{sanitizeInput(row.gatename)}</td>
                        <td className="py-3 px-4 border-start"></td>
                        <td className="py-3 px-4 fs-13 fw-500">{sanitizeInput(row.gate_in)}</td>
                        <td className="py-3 px-4 border-start"></td>
                        <td className="py-3 px-4 fs-13 fw-500">{sanitizeInput(row.gate_out)}</td>
                        <td className="py-3 px-4 border-start"></td>
                      </tr>
                    ))}
                  {(!customer.gate || customer.gate.length === 0) && (
                    <tr>
                      <td colSpan="6" className="text-center py-3 text-muted fs-13">
                        No gate details found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}

            {activeTab === "contacts" && (
              <Table hover className="m-0">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Name</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Mobile</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Email</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Type</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Available Time</th>
                    <th className="py-3 px-4 text-muted text-uppercase fs-11 fw-bold border-0">Primary</th>
                  </tr>
                </thead>
                <tbody>
                  {customer.contact &&
                    customer.contact.map((row, index) => (
                      <tr key={index} className="bg-white border-bottom">
                        <td className="py-3 px-4 fs-13 fw-500">{sanitizeInput(row.name)}</td>
                        <td className="py-3 px-4 fs-13 fw-500">{sanitizeInput(row.contact_number)}</td>
                        <td className="py-3 px-4 fs-13 fw-500">{sanitizeInput(row.email)}</td>
                        <td className="py-3 px-4 fs-13 fw-500">{sanitizeInput(row.contactname)}</td>
                        <td className="py-3 px-4 fs-13 fw-500">
                          {sanitizeInput(row.available_in_time)} - {sanitizeInput(row.available_out_time)}
                        </td>
                        <td className="py-3 px-4 fs-13 fw-500">{row.priority ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                      </tr>
                    ))}
                  {(!customer.contact || customer.contact.length === 0) && (
                    <tr>
                      <td colSpan="6" className="text-center py-3 text-muted fs-13">
                        No contact details found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}

            {activeTab === "attachments" && (
              <div className="bg-white rounded p-4">
                {customer.Attachments && customer.Attachments.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {customer.Attachments.map((attachment, index) => (
                      <div key={attachment.id || index} className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded d-flex align-items-center justify-content-center"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#E8F0FE",
                            }}
                          >
                            <FiFileText size={20} color="#3469E7" />
                          </div>
                          <div>
                            <div className="fw-500 fs-14">{sanitizeInput(attachment.file_name)}</div>
                            <div className="text-muted fs-12">{sanitizeInput(attachment.file_size)}</div>
                          </div>
                        </div>
                        <Button
                          variant="light"
                          className="btn-sm border d-flex align-items-center gap-2"
                          onClick={() => handleDownload(attachment.url, attachment.file_name)}
                        >
                          <FiDownload size={16} />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <FiFileText size={48} className="mb-3" style={{ opacity: 0.3 }} />
                    <p className="fs-14">No attachments found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal isOpen={isOpenDelete} onClose={() => setIsOpenDelete(false)} onDelete={handleDelete} title="Delete Customer" message={`Are you sure you want to delete "${sanitizeInput(customer?.first_name)}"? This action cannot be undone.`} />

      <CommonSnackbar show={snackbar.show} message={snackbar.message} isSuccess={snackbar.status} onClose={handleSnackbarClose} />
    </div>
  );
};

export default CustomerView;
